import { useEffect, useRef } from 'react';
import Sortable from 'sortablejs';

/*
    Drag to reorder a list, on the same library the Angular `sortable` directive used. The lesson
    list of a chapter had it, and arrows alone make moving a lesson from twelfth to first an
    eleven-click job.

    SortableJS moves the dragged node itself, which would leave the DOM and React's idea of the
    order disagreeing. So the move is undone before `onMove` runs: React re-renders from the form
    state, and that render is the only thing that decides where the row ends up.

    The arrow buttons stay. A drag handle is a mouse-only affordance, and these rows are also
    reachable by keyboard.
*/
export function useSortableList<Element extends HTMLElement>(onMove: (from: number, to: number) => void) {
    const listRef = useRef<Element | null>(null);

    // Through a ref, so re-creating the handler on each render does not tear down the Sortable.
    const onMoveRef = useRef(onMove);
    onMoveRef.current = onMove;

    useEffect(() => {
        const list = listRef.current;
        if (!list) return undefined;

        const sortable = Sortable.create(list, {
            // Only the handle starts a drag, or pressing a button in the row would begin one.
            handle: '[data-drag-handle]',
            animation: 150,
            onEnd: event => {
                const { oldIndex, newIndex, item, from } = event;
                if (oldIndex === undefined || newIndex === undefined || oldIndex === newIndex) return;

                // Put the node back where it started, then let the form state drive the re-render.
                from.removeChild(item);
                from.insertBefore(item, from.children[oldIndex] || null);

                onMoveRef.current(oldIndex, newIndex);
            },
        });

        return () => sortable.destroy();
    }, []);

    return listRef;
}
